import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/components/ui/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, Crown, Gift, Plus, Trophy, Users, Target, UserPlus, Upload, ImageIcon } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";

interface Competition {
  id: string;
  tenant_id: string;
  title: string;
  description?: string;
  image_url?: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
  is_completed: boolean;
  max_winners: number;
  rules_text?: string;
  is_multi_tenant: boolean;
  creator_tenant_id?: string;
  created_at: string;
  updated_at: string;
}

interface CompetitionParticipant {
  participant_id: string;
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  role: string;
  status: string;
  invited_at: string;
  accepted_at?: string;
}

interface CompetitionPrize {
  id: string;
  competition_id: string;
  position: number;
  title: string;
  description?: string;
  value?: string;
  image_url?: string;
  created_at: string;
  updated_at: string;
}

interface CompetitionEntry {
  id: string;
  competition_id: string;
  customer_id: string;
  referral_lead_id: string;
  entry_date: string;
  created_at: string;
  customer?: {
    id: string;
    full_name: string;
    email: string;
  };
}

interface CompetitionWinner {
  id: string;
  competition_id: string;
  customer_id: string;
  prize_id: string;
  position: number;
  customer_name: string;
  prize_title: string;
}

export default function CompetitionManager() {
  const { user } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<Competition[]>([]);
  const [prizes, setPrizes] = useState<{ [key: string]: CompetitionPrize[] }>({});
  const [entries, setEntries] = useState<{ [key: string]: CompetitionEntry[] }>({});
  const [winners, setWinners] = useState<{ [key: string]: CompetitionWinner[] }>({});
  const [participants, setParticipants] = useState<{ [key: string]: CompetitionParticipant[] }>({});
  const [tenantId, setTenantId] = useState<string>('');
  
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  
  const [newCompetition, setNewCompetition] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    rules_text: '',
    image_url: '',
    is_multi_tenant: false
  });
  const [newPrizes, setNewPrizes] = useState([
    { position: 1, title: '', description: '', value: '', image_url: '' },
    { position: 2, title: '', description: '', value: '', image_url: '' },
    { position: 3, title: '', description: '', value: '', image_url: '' }
  ]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [emailVerification, setEmailVerification] = useState<{
    [competitionId: string]: {
      email: string;
      tenantName: string;
      tenantSlug: string;
      verified: boolean;
    } | null;
  }>({});
  const [competitionImage, setCompetitionImage] = useState<File | null>(null);
  const [prizeImages, setPrizeImages] = useState<(File | null)[]>([null, null, null]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (user?.id) {
      fetchTenantId();
    }
  }, [user?.id]);

  useEffect(() => {
    if (tenantId) {
      fetchCompetitions();
    }
  }, [tenantId]);

  const verifyAdminEmail = async (email: string, competitionId: string) => {
    if (!email.trim()) {
      setEmailVerification(prev => ({ ...prev, [competitionId]: null }));
      return;
    }

    try {
      // Use the secure database function to verify admin email
      const { data: result, error } = await supabase.rpc('verify_admin_email', {
        p_email: email.trim(),
        p_inviter_user_id: user?.id
      });

      console.log('Admin verification result:', result);
      console.log('Verification error:', error);

      if (error) {
        console.error('Error verifying admin email:', error);
        setEmailVerification(prev => ({ ...prev, [competitionId]: null }));
        return;
      }

      const verificationResult = result as {
        exists: boolean;
        tenant_name?: string;
        tenant_slug?: string;
        same_tenant?: boolean;
        message?: string;
      } | null;

      if (verificationResult?.exists) {
        if (verificationResult.same_tenant) {
          // User is trying to invite their own tenant
          setEmailVerification(prev => ({ 
            ...prev, 
            [competitionId]: { 
              email: email.trim(),
              tenantName: `${verificationResult.tenant_name} (Ihr eigener Tenant)`,
              tenantSlug: verificationResult.tenant_slug || '',
              verified: false
            }
          }));
        } else {
          // Valid admin from different tenant
          setEmailVerification(prev => ({
            ...prev,
            [competitionId]: {
              email: email.trim(),
              tenantName: verificationResult.tenant_name || '',
              tenantSlug: verificationResult.tenant_slug || '',
              verified: true
            }
          }));
        }
      } else {
        // Email not found or not an admin
        setEmailVerification(prev => ({ ...prev, [competitionId]: null }));
      }
    } catch (error) {
      console.error('Error verifying admin email:', error);
      setEmailVerification(prev => ({ ...prev, [competitionId]: null }));
    }
  };

  const fetchTenantId = async () => {
    const { data } = await supabase
      .from('tenants')
      .select('id')
      .eq('admin_user_id', user!.id)
      .single();

    if (data) {
      setTenantId(data.id);
    }
  };

  const fetchCompetitions = async () => {
    setLoading(true);
    
    // Get competitions where this tenant is the owner
    const { data: ownedCompetitions } = await supabase
      .from('competitions')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false });

    // Get competitions where this tenant is an accepted participant
    const { data: participantCompetitions } = await supabase
      .from('competitions')
      .select(`
        *,
        competition_participants!inner(
          status,
          tenant_id
        )
      `)
      .eq('competition_participants.tenant_id', tenantId)
      .eq('competition_participants.status', 'accepted')
      .order('created_at', { ascending: false });

    // Get competitions where this tenant has pending invitations
    const { data: pendingCompetitions } = await supabase
      .from('competitions')
      .select(`
        *,
        competition_participants!inner(
          status,
          tenant_id
        )
      `)
      .eq('competition_participants.tenant_id', tenantId)
      .eq('competition_participants.status', 'pending')
      .order('created_at', { ascending: false });

    // Combine and deduplicate accepted competitions
    const allCompetitions = [...(ownedCompetitions || [])];
    if (participantCompetitions) {
      participantCompetitions.forEach(comp => {
        if (!allCompetitions.find(existing => existing.id === comp.id)) {
          allCompetitions.push(comp);
        }
      });
    }

    // Set pending invitations separately
    setPendingInvitations(pendingCompetitions || []);

    if (allCompetitions.length > 0 || pendingCompetitions?.length) {
      setCompetitions(allCompetitions);
      
      const allCompetitionIds = [
        ...allCompetitions.map(comp => comp.id),
        ...(pendingCompetitions || []).map(comp => comp.id)
      ];
      
      await Promise.all([
        fetchPrizes(allCompetitionIds),
        fetchEntries(allCompetitionIds),
        fetchWinners(allCompetitionIds),
        fetchParticipants(allCompetitionIds)
      ]);
    } else {
      setCompetitions([]);
      setPendingInvitations([]);
    }
    setLoading(false);
  };

  const fetchPrizes = async (competitionIds: string[]) => {
    const { data } = await supabase
      .from('competition_prizes')
      .select('*')
      .in('competition_id', competitionIds)
      .order('position');

    if (data) {
      const prizesByCompetition = data.reduce((acc, prize) => {
        if (!acc[prize.competition_id]) acc[prize.competition_id] = [];
        acc[prize.competition_id].push(prize);
        return acc;
      }, {} as { [key: string]: CompetitionPrize[] });
      
      setPrizes(prizesByCompetition);
    }
  };

  const fetchEntries = async (competitionIds: string[]) => {
    const { data } = await supabase
      .from('competition_entries')
      .select('*')
      .in('competition_id', competitionIds)
      .order('entry_date', { ascending: false });

    if (data) {
      // Fetch customer details for entries
      const customerIds = data.map(entry => entry.customer_id);
      const uniqueCustomerIds = [...new Set(customerIds)];
      
      const { data: customerData } = await supabase
        .from('customers')
        .select('id, full_name, email')
        .in('id', uniqueCustomerIds);

      const customerMap = (customerData || []).reduce((acc, customer) => {
        acc[customer.id] = customer;
        return acc;
      }, {} as { [key: string]: { id: string; full_name: string; email: string } });

      const entriesWithCustomers = data.map(entry => ({
        ...entry,
        customer: customerMap[entry.customer_id]
      }));

      const entriesByCompetition = entriesWithCustomers.reduce((acc, entry) => {
        if (!acc[entry.competition_id]) acc[entry.competition_id] = [];
        acc[entry.competition_id].push(entry);
        return acc;
      }, {} as { [key: string]: CompetitionEntry[] });
      
      setEntries(entriesByCompetition);
    }
  };

  const fetchWinners = async (competitionIds: string[]) => {
    const { data } = await supabase
      .from('competition_winners')
      .select('*')
      .in('competition_id', competitionIds);

    if (data) {
      // Fetch prize and customer details separately
      const prizeIds = data.map(winner => winner.prize_id);
      const customerIds = data.map(winner => winner.customer_id);

      const [prizeData, customerData] = await Promise.all([
        supabase.from('competition_prizes').select('id, title').in('id', prizeIds),
        supabase.from('customers').select('id, full_name').in('id', customerIds)
      ]);

      const prizeMap = (prizeData.data || []).reduce((acc, prize) => {
        acc[prize.id] = prize;
        return acc;
      }, {} as { [key: string]: { id: string; title: string } });

      const customerMap = (customerData.data || []).reduce((acc, customer) => {
        acc[customer.id] = customer;
        return acc;
      }, {} as { [key: string]: { id: string; full_name: string } });

      const winnersWithDetails = data.map(winner => ({
        ...winner,
        prize_title: prizeMap[winner.prize_id]?.title || '',
        customer_name: customerMap[winner.customer_id]?.full_name || ''
      }));

      const winnersByCompetition = winnersWithDetails.reduce((acc, winner) => {
        if (!acc[winner.competition_id]) acc[winner.competition_id] = [];
        acc[winner.competition_id].push(winner);
        return acc;
      }, {} as { [key: string]: CompetitionWinner[] });
      
      setWinners(winnersByCompetition);
    }
  };

  const fetchParticipants = async (competitionIds: string[]) => {
    try {
      const participantsData = await Promise.all(
        competitionIds.map(async (competitionId) => {
          const { data } = await supabase.rpc('get_competition_participants', {
            competition_uuid: competitionId
          });
          return { competitionId, participants: data || [] };
        })
      );

      const participantsByCompetition = participantsData.reduce((acc, { competitionId, participants }) => {
        acc[competitionId] = participants;
        return acc;
      }, {} as { [key: string]: CompetitionParticipant[] });

      setParticipants(participantsByCompetition);
    } catch (error) {
      console.error('Error fetching participants:', error);
    }
  };

  const inviteTenant = async (competitionId: string) => {
    const verification = emailVerification[competitionId];
    
    if (!verification?.verified) {
      toast({
        title: 'Fehler',
        description: 'Bitte geben Sie eine gültige Admin-E-Mail-Adresse ein.',
        variant: 'destructive'
      });
      return;
    }

    setInviting(true);
    try {
      const { data, error } = await supabase.rpc('invite_tenant_to_competition', {
        competition_uuid: competitionId,
        tenant_code: verification.tenantSlug,
        inviting_user_uuid: user!.id
      });

      if (error) throw error;

      if ((data as any).error) {
        toast({
          title: 'Fehler',
          description: (data as any).error,
          variant: 'destructive'
        });
      } else {
        toast({
          title: 'Erfolg',
          description: `Einladung an ${verification.tenantName} wurde gesendet.`
        });
        setInviteEmail('');
        setEmailVerification(prev => ({ ...prev, [competitionId]: null }));
        fetchParticipants([competitionId]);
      }
    } catch (error) {
      console.error('Error inviting tenant:', error);
      toast({
        title: 'Fehler',
        description: 'Einladung konnte nicht gesendet werden.',
        variant: 'destructive'
      });
    } finally {
      setInviting(false);
    }
  };

  const acceptInvitation = async (competitionId: string, tenantId: string) => {
    try {
      const { data, error } = await supabase.rpc('accept_competition_invitation', {
        competition_uuid: competitionId,
        tenant_uuid: tenantId
      });

      if (error) throw error;

      if ((data as any).error) {
        toast({
          title: 'Fehler',
          description: (data as any).error,
          variant: 'destructive'
        });
      } else {
        toast({
          title: 'Erfolg',
          description: 'Einladung wurde angenommen.'
        });
        // Refresh competitions to move from pending to accepted
        fetchCompetitions();
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      toast({
        title: 'Fehler',
        description: 'Einladung konnte nicht angenommen werden.',
        variant: 'destructive'
      });
    }
  };

  const endCompetition = async (competitionId: string) => {
    try {
      const { error } = await supabase
        .from('competitions')
        .update({ 
          is_completed: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', competitionId);

      if (error) throw error;

      toast({
        title: "Erfolg",
        description: "Wettbewerb erfolgreich beendet!",
      });

      // Refresh competitions to reflect the change
      await fetchCompetitions();
    } catch (error) {
      console.error('Error ending competition:', error);
      toast({
        title: "Fehler",
        description: "Wettbewerb konnte nicht beendet werden.",
        variant: "destructive",
      });
    }
  };

  const handleImageUpload = async (file: File): Promise<string | null> => {
    if (!user?.id) return null;

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('reward-images')
        .upload(fileName, file);

      if (error) throw error;

      const { data: urlData } = supabase.storage
        .from('reward-images')
        .getPublicUrl(data.path);

      return urlData.publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: "Fehler",
        description: "Bild konnte nicht hochgeladen werden.",
        variant: "destructive",
      });
      return null;
    }
  };

  const createCompetition = async () => {
    if (!user?.id || !tenantId) return;

    try {
      setUploading(true);
      
      // Upload competition image if selected
      let competitionImageUrl = newCompetition.image_url;
      if (competitionImage) {
        competitionImageUrl = await handleImageUpload(competitionImage);
      }

      // Upload prize images
      const prizesWithImages = await Promise.all(
        newPrizes.map(async (prize, index) => {
          let imageUrl = prize.image_url;
          if (prizeImages[index]) {
            imageUrl = await handleImageUpload(prizeImages[index]!);
          }
          return {
            ...prize,
            image_url: imageUrl
          };
        })
      );

      // Insert competition
      const { data, error } = await supabase
        .from('competitions')
        .insert({
          tenant_id: tenantId,
          creator_tenant_id: tenantId,
          title: newCompetition.title,
          description: newCompetition.description,
          start_date: newCompetition.start_date,
          end_date: newCompetition.end_date,
          rules_text: newCompetition.rules_text,
          image_url: competitionImageUrl,
          is_multi_tenant: newCompetition.is_multi_tenant
        })
        .select()
        .single();

      if (error) throw error;

      // Create prizes
      const prizesWithCompetitionId = prizesWithImages.map(prize => ({
        ...prize,
        competition_id: data.id
      }));

      const { error: prizeError } = await supabase
        .from('competition_prizes')
        .insert(prizesWithCompetitionId);

      if (prizeError) throw prizeError;

      if (newCompetition.is_multi_tenant) {
        const { error: participantError } = await supabase
          .from('competition_participants')
          .insert({
            competition_id: data.id,
            tenant_id: tenantId,
            role: 'creator',
            status: 'accepted',
            accepted_at: new Date().toISOString(),
            invited_by_user_id: user?.id
          });
        if (participantError) throw participantError;
      }

      toast({
        title: 'Erfolg',
        description: 'Wettbewerb wurde erfolgreich erstellt.'
      });

      setShowCreateDialog(false);
      setNewCompetition({
        title: '',
        description: '',
        start_date: '',
        end_date: '',
        rules_text: '',
        image_url: '',
        is_multi_tenant: false
      });
      setNewPrizes([
        { position: 1, title: '', description: '', value: '', image_url: '' },
        { position: 2, title: '', description: '', value: '', image_url: '' },
        { position: 3, title: '', description: '', value: '', image_url: '' }
      ]);
      setCompetitionImage(null);
      setPrizeImages([null, null, null]);
      fetchCompetitions();
    } catch (error) {
      console.error('Error creating competition:', error);
      const description = error instanceof Error ? error.message : 'Wettbewerb konnte nicht erstellt werden.';
      toast({
        title: 'Fehler',
        description,
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };

  const toggleCompetitionStatus = async (competitionId: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('competitions')
        .update({ is_active: isActive })
        .eq('id', competitionId);

      if (error) throw error;

      toast({
        title: 'Status geändert',
        description: `Wettbewerb wurde ${isActive ? 'aktiviert' : 'deaktiviert'}.`
      });

      fetchCompetitions();
    } catch (error) {
      console.error('Error toggling competition status:', error);
      toast({
        title: 'Fehler',
        description: 'Status konnte nicht geändert werden.',
        variant: 'destructive'
      });
    }
  };

  const drawWinners = async (competitionId: string) => {
    try {
      const { data, error } = await supabase.rpc('draw_competition_winners', {
        competition_uuid: competitionId
      });

      if (error) throw error;

      if ((data as any).error) {
        toast({
          title: 'Fehler',
          description: (data as any).error,
          variant: 'destructive'
        });
      } else {
        toast({
          title: 'Gewinner gezogen!',
          description: `${(data as any).winners.length} Gewinner wurden erfolgreich gezogen.`
        });
        fetchWinners([competitionId]);
        fetchCompetitions();
      }
    } catch (error) {
      console.error('Error drawing winners:', error);
      toast({
        title: 'Fehler',
        description: 'Gewinner konnten nicht gezogen werden.',
        variant: 'destructive'
      });
    }
  };

  const getCompetitionStats = (competitionId: string) => {
    const competitionEntries = entries[competitionId] || [];
    const uniqueParticipants = new Set(competitionEntries.map(entry => entry.customer_id)).size;
    const totalTickets = competitionEntries.length;
    
    return { uniqueParticipants, totalTickets };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Wettbewerbe</h1>
          <p className="text-muted-foreground">
            Verwalten Sie Ihre Kundenwettbewerbe und Gewinnspiele.
          </p>
        </div>
        
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Neuen Wettbewerb erstellen
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Neuen Wettbewerb erstellen</DialogTitle>
            </DialogHeader>
            
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="basic">Grunddaten</TabsTrigger>
                <TabsTrigger value="prizes">Preise</TabsTrigger>
              </TabsList>
              
              <TabsContent value="basic" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Titel *</Label>
                  <Input
                    id="title"
                    value={newCompetition.title}
                    onChange={(e) => setNewCompetition({ ...newCompetition, title: e.target.value })}
                    placeholder="Wettbewerb Titel"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Beschreibung</Label>
                  <Textarea
                    id="description"
                    value={newCompetition.description}
                    onChange={(e) => setNewCompetition({ ...newCompetition, description: e.target.value })}
                    placeholder="Beschreibung des Wettbewerbs..."
                    rows={3}
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Startdatum *</Label>
                    <Input
                      id="start_date"
                      type="datetime-local"
                      value={newCompetition.start_date}
                      onChange={(e) => setNewCompetition({ ...newCompetition, start_date: e.target.value })}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="end_date">Enddatum *</Label>
                    <Input
                      id="end_date"
                      type="datetime-local"
                      value={newCompetition.end_date}
                      onChange={(e) => setNewCompetition({ ...newCompetition, end_date: e.target.value })}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="rules_text">Teilnahmebedingungen</Label>
                  <Textarea
                    id="rules_text"
                    value={newCompetition.rules_text}
                    onChange={(e) => setNewCompetition({ ...newCompetition, rules_text: e.target.value })}
                    placeholder="Teilnahmebedingungen und Regelwerk..."
                    rows={4}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Wettbewerb Bild</Label>
                  <div className="flex items-center justify-center w-full">
                    <div className="flex flex-col items-center justify-center w-32 h-32 border-2 border-dashed border-muted-foreground/25 rounded-lg bg-muted/10">
                      {newCompetition.image_url ? (
                        <div className="space-y-2">
                          <img 
                            src={newCompetition.image_url} 
                            alt="Current" 
                            className="w-24 h-24 object-cover rounded-lg"
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const input = document.createElement('input')
                              input.type = 'file'
                              input.accept = 'image/*'
                              input.onchange = (e) => {
                                const file = (e.target as HTMLInputElement).files?.[0]
                                if (file) setCompetitionImage(file)
                              }
                              input.click()
                            }}
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            Bild ändern
                          </Button>
                        </div>
                      ) : competitionImage ? (
                        <div className="space-y-2">
                          <img 
                            src={URL.createObjectURL(competitionImage)} 
                            alt="Preview" 
                            className="w-24 h-24 object-cover rounded-lg"
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setCompetitionImage(null)}
                          >
                            Entfernen
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <ImageIcon className="h-12 w-12 mx-auto text-muted-foreground" />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const input = document.createElement('input')
                              input.type = 'file'
                              input.accept = 'image/*'
                              input.onchange = (e) => {
                                const file = (e.target as HTMLInputElement).files?.[0]
                                if (file) setCompetitionImage(file)
                              }
                              input.click()
                            }}
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            Bild hochladen
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/10">
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      <Label className="text-base font-medium">Multi-Tenant Wettbewerb</Label>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Erlauben Sie anderen Unternehmen, an diesem Wettbewerb teilzunehmen
                    </div>
                  </div>
                  <Switch
                    checked={newCompetition.is_multi_tenant}
                    onCheckedChange={(checked) => 
                      setNewCompetition({ ...newCompetition, is_multi_tenant: checked })
                    }
                  />
                </div>
              </TabsContent>
              
              <TabsContent value="prizes" className="space-y-4 mt-4">
                <div className="space-y-6">
                  {newPrizes.map((prize, index) => (
                    <Card key={prize.position}>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Crown className="w-5 h-5" />
                          {prize.position}. Platz
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Titel</Label>
                            <Input
                              value={prize.title}
                              onChange={(e) => {
                                const updated = [...newPrizes];
                                updated[index].title = e.target.value;
                                setNewPrizes(updated);
                              }}
                              placeholder="z.B. Hauptgewinn"
                            />
                          </div>
                          
                          <div className="space-y-2">
                            <Label>Wert</Label>
                            <Input
                              value={prize.value}
                              onChange={(e) => {
                                const updated = [...newPrizes];
                                updated[index].value = e.target.value;
                                setNewPrizes(updated);
                              }}
                              placeholder="z.B. 100€ Gutschein"
                            />
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Beschreibung</Label>
                          <Textarea
                            value={prize.description}
                            onChange={(e) => {
                              const updated = [...newPrizes];
                              updated[index].description = e.target.value;
                              setNewPrizes(updated);
                            }}
                            placeholder="Detaillierte Beschreibung des Preises..."
                            rows={2}
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Preis Bild</Label>
                          <div className="flex items-center justify-center w-full">
                            <div className="flex flex-col items-center justify-center w-32 h-32 border-2 border-dashed border-muted-foreground/25 rounded-lg bg-muted/10">
                              {prize.image_url ? (
                                <div className="space-y-2">
                                  <img 
                                    src={prize.image_url} 
                                    alt="Current" 
                                    className="w-24 h-24 object-cover rounded-lg"
                                  />
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      const input = document.createElement('input')
                                      input.type = 'file'
                                      input.accept = 'image/*'
                                      input.onchange = (e) => {
                                        const file = (e.target as HTMLInputElement).files?.[0]
                                        if (file) {
                                          const updated = [...prizeImages];
                                          updated[index] = file;
                                          setPrizeImages(updated);
                                        }
                                      }
                                      input.click()
                                    }}
                                  >
                                    <Upload className="h-4 w-4 mr-2" />
                                    Bild ändern
                                  </Button>
                                </div>
                              ) : prizeImages[index] ? (
                                <div className="space-y-2">
                                  <img 
                                    src={URL.createObjectURL(prizeImages[index]!)} 
                                    alt="Preview" 
                                    className="w-24 h-24 object-cover rounded-lg"
                                  />
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      const updated = [...prizeImages];
                                      updated[index] = null;
                                      setPrizeImages(updated);
                                    }}
                                  >
                                    Entfernen
                                  </Button>
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  <ImageIcon className="h-8 w-8 mx-auto text-muted-foreground" />
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      const input = document.createElement('input')
                                      input.type = 'file'
                                      input.accept = 'image/*'
                                      input.onchange = (e) => {
                                        const file = (e.target as HTMLInputElement).files?.[0]
                                        if (file) {
                                          const updated = [...prizeImages];
                                          updated[index] = file;
                                          setPrizeImages(updated);
                                        }
                                      }
                                      input.click()
                                    }}
                                  >
                                    <Upload className="h-4 w-4 mr-2" />
                                    Bild hochladen
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
            
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Abbrechen
              </Button>
              <Button onClick={createCompetition} disabled={uploading}>
                {uploading ? 'Speichere...' : 'Wettbewerb erstellen'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Competitions List */}
      <div className="grid gap-6">
        {/* Pending Invitations Section */}
        {pendingInvitations.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">Ausstehende Einladungen</h2>
              <Badge variant="secondary">{pendingInvitations.length}</Badge>
            </div>
            {pendingInvitations.map((competition) => {
              const competitionPrizes = prizes[competition.id] || [];
              
              return (
                <Card key={competition.id} className="border-yellow-200 bg-yellow-50/50">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <CardTitle>{competition.title}</CardTitle>
                          <Badge variant="outline" className="border-yellow-500 text-yellow-700">
                            Einladung ausstehend
                          </Badge>
                        </div>
                        
                        <CardDescription>{competition.description}</CardDescription>
                        
                        <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {format(new Date(competition.start_date), 'dd.MM.yyyy', { locale: de })} - 
                            {format(new Date(competition.end_date), 'dd.MM.yyyy', { locale: de })}
                          </div>
                          <div className="flex items-center gap-1">
                            <Gift className="w-4 h-4" />
                            {competitionPrizes.length} Preise
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={() => acceptInvitation(competition.id, tenantId)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          Annehmen
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  
                  {competitionPrizes.length > 0 && (
                    <CardContent>
                      <div className="space-y-2">
                        <h4 className="font-medium">Preise:</h4>
                        <div className="grid gap-2">
                          {competitionPrizes.slice(0, 3).map((prize) => (
                            <div key={prize.id} className="flex items-center gap-3 p-2 bg-white rounded border">
                              <div className="flex items-center justify-center w-6 h-6 bg-primary/10 rounded-full">
                                <span className="text-xs font-bold text-primary">{prize.position}</span>
                              </div>
                              <div className="flex-1">
                                <p className="text-sm font-medium">{prize.title}</p>
                                {prize.value && (
                                  <p className="text-xs text-primary">{prize.value}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {/* Active Competitions Section */}
        {competitions.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Meine Wettbewerbe</h2>
          </div>
        )}

        {competitions.length === 0 && pendingInvitations.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Trophy className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Keine Wettbewerbe vorhanden</h3>
              <p className="text-muted-foreground mb-6">
                Erstellen Sie Ihren ersten Wettbewerb für Ihre Kunden.
              </p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Ersten Wettbewerb erstellen
              </Button>
            </CardContent>
          </Card>
        ) : (
          competitions.map((competition) => {
            const stats = getCompetitionStats(competition.id);
            const competitionPrizes = prizes[competition.id] || [];
            const competitionWinners = winners[competition.id] || [];
            const isExpired = new Date(competition.end_date) < new Date();
            
            return (
              <Card key={competition.id}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <CardTitle>{competition.title}</CardTitle>
                        <Badge variant={competition.is_active ? 'default' : 'secondary'}>
                          {competition.is_active ? 'Aktiv' : 'Inaktiv'}
                        </Badge>
                        {competition.is_completed && (
                          <Badge variant="outline">
                            <Trophy className="w-3 h-3 mr-1" />
                            Abgeschlossen
                          </Badge>
                        )}
                        {isExpired && !competition.is_completed && (
                          <Badge variant="destructive">Abgelaufen</Badge>
                        )}
                      </div>
                      
                      <CardDescription>{competition.description}</CardDescription>
                      
                      <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {format(new Date(competition.start_date), 'dd.MM.yyyy', { locale: de })} - 
                          {format(new Date(competition.end_date), 'dd.MM.yyyy', { locale: de })}
                        </div>
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {stats.uniqueParticipants} Teilnehmer
                        </div>
                        <div className="flex items-center gap-1">
                          <Gift className="w-4 h-4" />
                          {stats.totalTickets} Lose
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {/* Only show end button for owned competitions that are not completed */}
                      {competition.tenant_id === tenantId && !competition.is_completed && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => endCompetition(competition.id)}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          Beenden
                        </Button>
                      )}
                      <Switch
                        checked={competition.is_active}
                        onCheckedChange={(checked) => toggleCompetitionStatus(competition.id, checked)}
                        disabled={competition.is_completed}
                      />
                      
                      {!competition.is_completed && isExpired && (
                        <Button
                          size="sm"
                          onClick={() => drawWinners(competition.id)}
                        >
                          <Target className="w-4 h-4 mr-2" />
                          Gewinner ziehen
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <Tabs defaultValue="prizes" className="w-full">
                    <TabsList className="grid grid-cols-4 w-full">
                      <TabsTrigger value="prizes">Preise</TabsTrigger>
                      <TabsTrigger value="participants">Teilnehmer</TabsTrigger>
                      {competition.is_multi_tenant && (
                        <TabsTrigger value="tenants">Tenants</TabsTrigger>
                      )}
                      <TabsTrigger value="winners">Gewinner</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="prizes" className="space-y-4 mt-4">
                      {competitionPrizes.length === 0 ? (
                        <div className="text-center p-6">
                          <p className="text-muted-foreground">Keine Preise definiert</p>
                        </div>
                      ) : (
                        <div className="grid gap-4">
                          {competitionPrizes.map((prize) => (
                            <div key={prize.id} className="flex items-center gap-4 p-4 border rounded-lg">
                              <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-full">
                                <span className="text-sm font-bold text-primary">{prize.position}</span>
                              </div>
                              {prize.image_url && (
                                <img 
                                  src={prize.image_url} 
                                  alt={prize.title} 
                                  className="w-12 h-12 object-cover rounded-lg"
                                />
                              )}
                              <div className="flex-1">
                                <h4 className="font-semibold">{prize.title}</h4>
                                {prize.description && (
                                  <p className="text-sm text-muted-foreground">{prize.description}</p>
                                )}
                                {prize.value && (
                                  <p className="text-sm font-medium text-primary">{prize.value}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </TabsContent>
                    
                    <TabsContent value="participants" className="space-y-4 mt-4">
                      {entries[competition.id]?.length === 0 ? (
                        <div className="text-center p-6">
                          <p className="text-muted-foreground">Noch keine Teilnehmer</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {entries[competition.id]?.map((entry) => (
                            <div key={entry.id} className="flex items-center justify-between p-3 border rounded">
                              <div>
                                <p className="font-medium">{entry.customer?.full_name}</p>
                                <p className="text-sm text-muted-foreground">{entry.customer?.email}</p>
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {format(new Date(entry.entry_date), 'dd.MM.yyyy HH:mm', { locale: de })}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </TabsContent>
                    
                    {competition.is_multi_tenant && (
                      <TabsContent value="tenants" className="space-y-4 mt-4">
                        <div className="space-y-4">
                          <div className="space-y-3">
                            <Label htmlFor={`invite-email-${competition.id}`}>
                              Admin E-Mail-Adresse
                            </Label>
                            <Input
                              id={`invite-email-${competition.id}`}
                              type="email"
                              placeholder="admin@unternehmen.de"
                              value={inviteEmail}
                              onChange={(e) => {
                                setInviteEmail(e.target.value);
                                verifyAdminEmail(e.target.value, competition.id);
                              }}
                              className="flex-1"
                            />
                            
                            {emailVerification[competition.id]?.verified && (
                              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-medium text-green-800">
                                      ✓ Admin gefunden
                                    </p>
                                    <p className="text-sm text-green-600">
                                      Unternehmen: {emailVerification[competition.id]?.tenantName}
                                    </p>
                                  </div>
                                  <Button
                                    onClick={() => inviteTenant(competition.id)}
                                    disabled={inviting}
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    <UserPlus className="w-4 h-4 mr-2" />
                                    Einladung senden
                                  </Button>
                                </div>
                              </div>
                            )}
                            
                            {emailVerification[competition.id] && !emailVerification[competition.id]?.verified && emailVerification[competition.id]?.tenantName === 'Ihr eigener Tenant' && (
                              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                <p className="text-sm text-yellow-800">
                                  ⚠️ Das ist Ihr eigener Tenant - Sie können sich nicht selbst einladen.
                                </p>
                              </div>
                            )}
                            
                            {inviteEmail && !emailVerification[competition.id] && (
                              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                                <p className="text-sm text-gray-600">
                                  Kein Admin mit dieser E-Mail-Adresse gefunden.
                                </p>
                              </div>
                            )}
                          </div>
                          
                          <div className="text-sm text-muted-foreground">
                            💡 Geben Sie die E-Mail-Adresse des Administrators ein, den Sie einladen möchten.
                          </div>
                        </div>
                        
                        
                        {participants[competition.id]?.length === 0 ? (
                          <div className="text-center p-6">
                            <p className="text-muted-foreground">Noch keine teilnehmenden Tenants</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {participants[competition.id]?.map((participant) => (
                              <div key={participant.participant_id} className="flex items-center justify-between p-3 border rounded">
                                <div>
                                  <p className="font-medium">{participant.tenant_name}</p>
                                  <p className="text-sm text-muted-foreground">
                                    Rolle: {participant.role} | Status: {participant.status}
                                  </p>
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {participant.accepted_at ? 
                                    format(new Date(participant.accepted_at), 'dd.MM.yyyy', { locale: de }) :
                                    'Ausstehend'
                                  }
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </TabsContent>
                    )}
                    
                    <TabsContent value="winners" className="space-y-4 mt-4">
                      {competitionWinners.length === 0 ? (
                        <div className="text-center p-6">
                          <p className="text-muted-foreground">
                            {competition.is_completed ? 'Keine Gewinner ermittelt' : 'Noch keine Gewinner gezogen'}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {competitionWinners.map((winner) => (
                            <div key={winner.id} className="flex items-center gap-4 p-4 border rounded-lg bg-yellow-50/50">
                              <div className="flex items-center justify-center w-8 h-8 bg-yellow-500 text-white rounded-full">
                                <span className="text-sm font-bold">{winner.position}</span>
                              </div>
                              <div className="flex-1">
                                <p className="font-medium">{winner.customer_name}</p>
                                <p className="text-sm text-muted-foreground">{winner.prize_title}</p>
                              </div>
                              <Trophy className="w-5 h-5 text-yellow-500" />
                            </div>
                          ))}
                        </div>
                      )}
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
